import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = process.env.REACT_APP_LOGO_URL || "https://customer-assets.emergentagent.com/job_closings-concierge/artifacts/16ywe7kj_ChatGPT%20Image%20Feb%2026%2C%202026%2C%2007_04_50%20PM.png";

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'How It Works', path: '/how-it-works' },
  { name: 'Services', path: '/services' },
  { name: 'Pricing', path: '/pricing' },
  { name: 'Partners', path: '/partners' },
  { name: 'FAQ', path: '/faq' },
  { name: 'About', path: '/about' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-black/80 backdrop-blur-xl border-b border-white/5'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-56">
          {/* Logo */}
          <Link to="/" data-testid="header-logo-link" className="flex-shrink-0">
            <img
              src={LOGO_URL}
              alt="Better Closings"
              className="h-48 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`text-xs uppercase tracking-[0.15em] font-medium transition-colors duration-300 whitespace-nowrap ${
                  location.pathname === link.path
                    ? 'text-[#C9A227]'
                    : 'text-white/80 hover:text-[#C9A227]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link to="/contact" data-testid="header-cta-link">
              <Button
                data-testid="header-book-demo-btn"
                className="bg-[#C9A227] text-black hover:bg-[#E5D5A5] rounded-none px-6 py-2 font-semibold text-xs tracking-wider uppercase transition-all duration-300"
              >
                Book a Demo
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-black/95 backdrop-blur-xl border-b border-white/5"
            data-testid="mobile-menu"
          >
            <nav className="px-6 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`block text-sm uppercase tracking-[0.15em] font-medium py-2 transition-colors duration-300 ${
                    location.pathname === link.path
                      ? 'text-[#C9A227]'
                      : 'text-white/80 hover:text-[#C9A227]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/contact" className="block pt-4" data-testid="mobile-cta-link">
                <Button
                  data-testid="mobile-book-demo-btn"
                  className="w-full bg-[#C9A227] text-black hover:bg-[#E5D5A5] rounded-none px-6 py-3 font-semibold text-xs tracking-wider uppercase"
                >
                  Book a Demo
                </Button>
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
